<template>
  <div v-if="isOpen" class="modal-container">
    <div class="modal-black-cover" @click="handleCancel"></div>
    <div class="modal-content-layout">
      <div class="modal-content-container min-w-[320px] sm:min-w-[364px] sm:max-w-[392px] !max-h-[100vh] flex flex-col overflow-hidden">
        <!-- Fixed Header -->
        <div class="flex-shrink-0 px-6 py-4 border-b border-gray-500">
          <div class="flex justify-between items-center mb-2">
            <h2 class="text-20px font-medium">{{ $t('profiles.backed_campaigns.shipping_info') }}</h2>
            <button @click="handleCancel" class="text-gray-400 hover:text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <hr class="mx-auto border-t-0 w-16 h-1 bg-primary" />
        </div>

        <!-- Scrollable Form Content -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <form @submit.prevent="handleSubmit" class="space-y-4 max-w-md">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label for="shipping-name" class="block text-14px font-bold mb-2 text-white">
                {{ $t('campaign_cart.index.contact_name') }} <span class="text-orange-300">*</span>
              </label>
              <input
                id="shipping-name"
                v-model="form.name"
                type="text"
                class="w-full default-input-klass !border-gray-150"
                required
                autocomplete="name" />
            </div>

            <div>
              <label for="shipping-phone" class="block text-14px font-bold mb-2 text-white">
                {{ $t('campaign_cart.index.phone') }} <span class="text-orange-300">*</span>
              </label>
              <input
                id="shipping-phone"
                v-model="form.phone"
                type="tel"
                class="w-full default-input-klass !border-gray-150"
                required
                autocomplete="tel" />
            </div>
          </div>

          <div>
            <label for="shipping-country" class="block text-14px font-bold mb-2 text-white">
              {{ $t('campaign_cart.index.country_region') }}
            </label>
            <div class="w-full default-input-klass border border-gray-150 p-2 rounded-[8px] cursor-not-allowed">
              {{ form.country || '-' }}
            </div>
          </div>

          <div>
            <label for="shipping-postal-code" class="block text-14px font-bold mb-2 text-white">
              {{ $t('campaign_cart.index.zip_postal_code') }} <span class="text-orange-300">*</span>
            </label>
            <input
              id="shipping-postal-code"
              v-model="form.zip"
              type="text"
              class="w-full default-input-klass !border-gray-150"
              required
              autocomplete="postal-code" />
          </div>

          <div>
            <label for="shipping-state" class="block text-14px font-bold mb-2 text-white">
              {{ $t('campaign_cart.index.state_province_region') }} <span class="text-orange-300">*</span>
            </label>
            <input
              id="shipping-state"
              v-model="form.state"
              type="text"
              class="w-full default-input-klass !border-gray-150"
              required
              autocomplete="address-level1" />
          </div>

          <div>
            <label for="shipping-city" class="block text-14px font-bold mb-2 text-white">
              {{ $t('campaign_cart.index.town_city') }} <span class="text-orange-300">*</span>
            </label>
            <input
              id="shipping-city"
              v-model="form.city"
              type="text"
              class="w-full default-input-klass !border-gray-150"
              required
              autocomplete="address-level2" />
          </div>

          <div>
            <label for="shipping-address1" class="block text-14px font-bold mb-2 text-white">
              {{ $t('campaign_cart.index.address_line_1') }} <span class="text-orange-300">*</span>
            </label>
            <input
              id="shipping-address1"
              v-model="form.address1"
              type="text"
              class="w-full default-input-klass !border-gray-150"
              required
              autocomplete="address-line1" />
          </div>

          <div>
            <label for="shipping-address2" class="block text-14px font-bold mb-2 text-white">
              {{ $t('campaign_cart.index.address_line_2') }}
            </label>
            <input
              id="shipping-address2"
              v-model="form.address2"
              type="text"
              class="w-full default-input-klass !border-gray-150"
              autocomplete="address-line2" />
          </div>

            <!-- Error Message -->
            <div v-if="error" class="text-red-400 text-sm text-center">
              {{ error }}
            </div>
          </form>
        </div>

        <!-- Fixed Footer -->
        <div class="flex-shrink-0 px-6 py-4 border-t border-gray-500">
          <div class="grid grid-cols-2 gap-4 max-w-md">
            <button
              type="button"
              @click="handleCancel"
              class="btn btn-secondary">
              {{ $t('profiles.backed_campaigns.modal.cancel') }}
            </button>
            <button
              type="submit"
              @click="handleSubmit"
              :disabled="loading"
              class="btn btn-primary">
              {{ loading ? $t('profiles.backed_campaigns.modal.saving') : $t('profiles.backed_campaigns.modal.submit') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, watch, onUpdated, computed } from 'vue'

export default defineComponent({
  name: 'ShippingModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
  },
  emits: ['close', 'submit'],
  setup(props, { emit }) {
    const form = reactive({
      country: '',
      country_code: '',
      zip: '',
      state: '',
      city: '',
      address1: '',
      address2: '',
      name: '',
      phone: ''
    })

    // 當 modal 開啟且有初始資料時，填入表單
    watch(() => [props.isOpen, props.initialData], ([isOpen, initialData]) => {
      if (isOpen && initialData) {
        form.country = initialData.country || ''
        form.country_code = initialData.country_code || ''
        form.zip = initialData.zip || ''
        form.state = initialData.state || ''
        form.city = initialData.city || ''
        form.address1 = initialData.address1 || ''
        form.address2 = initialData.address2 || ''
        form.name = initialData.name || ''
        form.phone = initialData.phone || ''
      }
    }, { immediate: true })

    const handleCancel = () => {
      // 清空表單
      Object.keys(form).forEach(key => {
        form[key] = ''
      })
      emit('close')
    }

    const handleSubmit = () => {
      emit('submit', { ...form })
    }


    onUpdated(() => {
      window.initUniformSelect()
    })

    return {
      form,
      handleCancel,
      handleSubmit
    }
  }
})
</script>